import {
  Copy,
  PencilSimpleLine,
  Trash,
  UserList,
  UserSwitch,
} from "@phosphor-icons/react";
import { type ReactElement, useState } from "react";

import DesktopActions from "common/components/DesktopActions";
import MobileActions from "common/components/MobileActions";
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { ActionsLeafKey, ActionsType } from "common/types";

import useLeadListMutations from "../../datahooks/useLeadListMutations";
import LeadListDelete from "./LeadListDelete";
import LeadListRename from "./LeadListRename";

import type { LeadList } from "../../types";

interface LeadListActionsProps {
  leadList: LeadList;
  children: ReactElement;
  contentClassName?: string;
}

export default function LeadListActions({
  leadList,
  children,
  contentClassName,
}: LeadListActionsProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [isOpen, setIsOpen] = useState(false);
  const { duplicateList } = useLeadListMutations();

  const { id, name } = leadList;

  function onDuplicate(duplicateSources: boolean) {
    duplicateList({ listId: id, name, duplicateSources });
  }

  const editOptions = {
    rename: { icon: PencilSimpleLine, label: "Rename" },
    duplicate: {
      icon: Copy,
      label: "Duplicate",
      subActions: {
        leadsOnly: {
          icon: UserList,
          label: "Duplicate with leads only",
          onAction: () => onDuplicate(false),
        },
        withSources: {
          icon: UserSwitch,
          label: "Duplicate with sources",
          onAction: () => onDuplicate(true),
        },
      },
    },
    delete: { icon: Trash, label: "Delete list", isDanger: true },
  } satisfies ActionsType;

  function renderAction(
    action: ActionsLeafKey<typeof editOptions>,
    onBack: () => void,
    onClose: () => void,
  ) {
    if (action === "rename") {
      return (
        <LeadListRename leadList={leadList} onBack={onBack} onClose={onClose} />
      );
    }

    return (
      <LeadListDelete leadList={leadList} onBack={onBack} onClose={onClose} />
    );
  }

  if (isDesktop) {
    return (
      <DesktopActions
        triggerElement={children}
        editOptions={editOptions}
        contentClassName={contentClassName}
      >
        {(action, onClose) => renderAction(action, onClose, onClose)}
      </DesktopActions>
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerTrigger asChild>{children}</DrawerTrigger>

      <DrawerContent>
        <MobileActions editOptions={editOptions} editTitle="List actions">
          {(action, onBack) =>
            renderAction(action, onBack, () => setIsOpen(false))
          }
        </MobileActions>
      </DrawerContent>
    </Drawer>
  );
}
